/* eslint-disable camelcase */
import type { ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useLocation, useParams } from 'react-router-dom'
import { clsx } from 'clsx'
import { ArrowLeft, Copy, ThumbsDown, ThumbsUp } from 'lucide-react'

import AudioSection from './AudioSection'
import { LIST_PATH, readListReturn } from './listLocation'
import Timestamp from './Timestamp'
import { errorStatus, normalizeFeedbackId, useSummaryFeedback } from './useSummaryFeedback'

import styles from './SummaryFeedbackPage.module.scss'

import type { SummaryFeedbackDetail } from '@/api/meetingSummaryFeedback'
import Button from '@/components/Button'
import { useToast } from '@/components/Toast/ToastContext'

const consentLabels: Record<SummaryFeedbackDetail['consentBasis'], string> = {
  disclosure_v1: 'Accepted the feedback disclosure (v1)',
  share_data_election: 'Opted in to data sharing',
  none: 'No consent',
}

const withheldNotices: Record<NonNullable<SummaryFeedbackDetail['summaryWithheldReason']>, string> = {
  no_consent: 'Not stored: the user had no disclosure or data-sharing consent. The hash is kept.',
  meeting_deleted: 'The meeting was deleted; its summary snapshot is no longer shown.',
  not_stored: 'No summary snapshot was stored for this rating. The hash is kept.',
}

const SHORT_SHA_LENGTH = 12
const EMPTY = '—'

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
)

const Sha = ({ value }: { value: string }) => (
  <code title={value}>{value.length > SHORT_SHA_LENGTH ? `${value.slice(0, SHORT_SHA_LENGTH)}…` : value}</code>
)

const Detail = ({ detail }: { detail: SummaryFeedbackDetail }) => {
  const { updateToast } = useToast()
  const RatingIcon = detail.rating === 'up' ? ThumbsUp : ThumbsDown
  const copyMeetingId = () => {
    if (!navigator.clipboard) {
      updateToast("Couldn't copy the meeting ID.", 'error')
      return
    }
    navigator.clipboard.writeText(detail.meetingId).then(
      () => updateToast('Meeting ID copied', 'success'),
      () => updateToast("Couldn't copy the meeting ID.", 'error')
    )
  }

  return (
    <>
      <header className={styles.header}>
        <p className={styles.eyebrow}>Summary feedback</p>
        <h1 className={clsx(styles.rating, detail.clearedAt && styles.muted)}>
          <RatingIcon size={28} aria-hidden="true" />
          {detail.rating === 'up' ? 'Thumbs up' : 'Thumbs down'}
        </h1>
      </header>

      {detail.clearedAt && (
        <p className={styles.banner}>
          Rating withdrawn <Timestamp value={detail.clearedAt} />
        </p>
      )}

      {detail.feedback ? (
        <blockquote className={styles.note}>{detail.feedback}</blockquote>
      ) : (
        <p className={styles.empty}>No note.</p>
      )}

      <dl className={styles.list}>
        <Row label="Reporter">
          {detail.reporterEmail ?? 'Unknown'}{' '}
          <span className={clsx(styles.tag, detail.reporterIsInternal && styles.tagInternal)}>
            {detail.reporterIsInternal ? 'Internal' : 'External'}
          </span>
        </Row>
        <Row label="Submitted (client clock)">
          <Timestamp value={detail.submittedAt} />
        </Row>
        <Row label="Received">
          <Timestamp value={detail.createdAt} />
        </Row>
        <Row label="Consent">{consentLabels[detail.consentBasis]}</Row>
        <Row label="Meeting ID">
          <span className={styles.copyable}>
            <code>{detail.meetingId}</code>
            <Button
              title={<Copy size={14} aria-hidden="true" />}
              ariaLabel="Copy meeting ID"
              type="light"
              className={styles.iconButton}
              onClick={copyMeetingId}
            />
          </span>
        </Row>
        <Row label="Feedback ID">
          <code>{detail.id}</code>
        </Row>
      </dl>

      <section className={styles.section} aria-labelledby="summary-feedback-provenance">
        <h2 id="summary-feedback-provenance">Provenance</h2>
        {detail.summaryDrift && (
          <p className={styles.warning}>
            Drift: the app rated a different summary than the one stored here, so the snapshot may not match what the
            user saw.
          </p>
        )}
        <dl className={styles.list}>
          <Row label="Model">{detail.summaryModel ?? EMPTY}</Row>
          <Row label="Summary prompt">
            {detail.summaryPromptVersion ? <Sha value={detail.summaryPromptVersion} /> : EMPTY}
          </Row>
          <Row label="Summary SHA-256">
            <Sha value={detail.summarySha256} />
          </Row>
          {detail.clientSummarySha256 && (
            <Row label="Client SHA-256">
              <Sha value={detail.clientSummarySha256} />
            </Row>
          )}
        </dl>
      </section>

      <section className={styles.section} aria-labelledby="summary-feedback-snapshot">
        <h2 id="summary-feedback-snapshot">Summary snapshot</h2>
        {detail.summaryWithheldReason ? (
          <p className={styles.notice}>{withheldNotices[detail.summaryWithheldReason]}</p>
        ) : detail.summaryBody !== null ? (
          <div className={styles.snapshot}>{detail.summaryBody}</div>
        ) : (
          <p className={styles.notice}>{withheldNotices.not_stored}</p>
        )}
      </section>

      <AudioSection detail={detail} />

      {detail.viewerMeetingId && (
        <nav className={styles.links} aria-label="Related">
          <Link to={`/app/attribution/${encodeURIComponent(detail.viewerMeetingId)}`}>Open in attribution viewer</Link>
        </nav>
      )}
    </>
  )
}

const NotFound = ({ id }: { id: string | undefined }) => (
  <>
    <header className={styles.header}>
      <p className={styles.eyebrow}>Summary feedback</p>
      <h1 className={styles.rating}>No summary feedback with this ID</h1>
    </header>
    {id && <code className={styles.notFoundId}>{id}</code>}
  </>
)

const SummaryFeedbackPage = () => {
  const { id } = useParams()
  const listReturn = readListReturn(useLocation().state)
  const feedbackId = normalizeFeedbackId(id)
  const query = useSummaryFeedback(feedbackId)
  const status = errorStatus(query.error)

  let body
  if (feedbackId === null || status === 404) {
    body = <NotFound id={id} />
  } else if (query.data) {
    body = <Detail detail={query.data} />
  } else if (status === 401 || status === 403) {
    body = (
      <div className={styles.state} role="alert">
        You need internal admin access to view summary feedback.
      </div>
    )
  } else if (query.isError) {
    body = (
      <div className={styles.state} role="alert">
        <span>Couldn&apos;t load this summary feedback.</span>
        <Button title="Retry" type="light" isLoading={query.isFetching} onClick={() => void query.refetch()} />
      </div>
    )
  } else {
    body = (
      <div className={styles.state} role="status">
        Loading summary feedback…
      </div>
    )
  }

  return (
    <main className={styles.page}>
      <Helmet>
        <title>Summary feedback</title>
      </Helmet>
      <div className={styles.content}>
        <Link to={`${LIST_PATH}${listReturn?.search ?? ''}`} state={listReturn?.listState} className={styles.backLink}>
          <ArrowLeft size={14} aria-hidden="true" />
          All summary feedback
        </Link>
        {body}
      </div>
    </main>
  )
}

export default SummaryFeedbackPage
